import { computed, shallowRef, watch } from "vue";
import Fuse from "fuse.js";
import { useSelectedTagsMode } from "#ce/components/SpQuantumSelect/composables/useSelectedTagsMode";
import { isRemoteSearch } from "#ce/components/SpQuantumSelect/props";
import { toBoolean } from "#ce/utils/props";
import { customItemTitle } from "#ce/components/SpSelect/i18n";
import { filterSearchResults } from "#ce/components/SpQuantumSelect/composables/useQuantumSelectItems/filter-items";
import {
  FUSE_OPTIONS,
  MIN_FUZZY_QUERY_LENGTH,
  rankFuzzyResults,
} from "#ce/components/SpQuantumSelect/composables/useQuantumSelectItems/rank-fuzzy-items";
import { searchStrings } from "#ce/components/SpQuantumSelect/composables/useQuantumSelectItems/search-helpers";

function flattenItems(items) {
  return items.flatMap((item) => [item, ...(Array.isArray(item?.children) ? flattenItems(item.children) : [])]);
}

/**
 * Offers a new value only when the picker's matching rules find no existing item. The offer is
 * transient; committing it adds an ordinary item to the select's lifetime list. Picker copies
 * use private title/value fields so caller data cannot become picker metadata.
 */
export function useCustomItems(
  props,
  { i18nReady, items, hasItems, selected, getItemTitle, getItemValue, findItem, addCustomItem },
) {
  const remote = computed(() => isRemoteSearch(props));
  const selectedTagsEnabled = useSelectedTagsMode(props);
  const search = shallowRef("");
  const allowCustom = computed(() => toBoolean(props.allowCustom) && !remote.value);
  const typedSearch = computed(() => allowCustom.value || remote.value);
  const pickerItemTitle = computed(() => (allowCustom.value ? "__spSelectTitle" : props.itemTitle));
  const pickerItemValue = computed(() => (allowCustom.value ? "__spSelectValue" : props.itemValue));
  const projectedItems = computed(() => (allowCustom.value ? items.value.map(withPickerFields) : items.value));
  const hasMatches = shallowRef(false);
  const flattenedItems = computed(() => flattenItems(projectedItems.value));
  const treeFuse = computed(() => {
    if (!allowCustom.value || !toBoolean(props.fuzzySearch)) {
      return null;
    }
    const entries = flattenedItems.value
      .filter((item) => item && !item.header && !item.pinned && !item.fallback)
      .flatMap((item) => searchStrings(item, getPickerItemTitle).map((text) => ({ item, text })));
    return new Fuse(entries, FUSE_OPTIONS);
  });
  const hasTreeMatch = computed(() => {
    const query = search.value.trim();
    if (!allowCustom.value || !query) {
      return false;
    }
    if (treeFuse.value && query.length >= MIN_FUZZY_QUERY_LENGTH) {
      const fuzzyResults = /^\d+$/.test(query) ? [] : treeFuse.value.search(query);
      return rankFuzzyResults(flattenedItems.value, query, fuzzyResults, getPickerItemTitle).hasMatches;
    }
    return filterSearchResults(flattenedItems.value, query, false, getPickerItemTitle).hasMatches;
  });
  const searchable = computed(() => toBoolean(props.searchable) || allowCustom.value || remote.value);
  const hideSearchBarAttr = computed(() => !searchable.value || (remote.value && props.inputmode !== "none") || null);
  const highlightIdleFirstResult = computed(() => toBoolean(props.highlightFirstResult) && !searchable.value);
  const pickerSearch = computed(() => (typedSearch.value ? search.value : props.search));
  const canOpenList = computed(() => hasItems.value || allowCustom.value || remote.value);
  const minQueryLength = computed(() => Number(props.minQueryLength) || 0);
  const mapResults = computed(() => (allowCustom.value || minQueryLength.value > 0 ? mapResultRows : undefined));
  const searchTooShort = computed(() => search.value.trim().length < minQueryLength.value);
  const noDataMessage = computed(() => (searchTooShort.value && props.minQueryText) || props.noDataText);

  const customItem = computed(() => {
    const text = search.value;

    if (
      !i18nReady.value ||
      !allowCustom.value ||
      !text.trim() ||
      hasMatches.value ||
      findItem(text) ||
      ((selectedTagsEnabled.value || toBoolean(props.selectionClearable)) &&
        selected().some((entry) => String(getItemValue(entry)) === text)) ||
      hasTreeMatch.value
    ) {
      return null;
    }

    return { [props.itemTitle]: text, [props.itemValue]: text };
  });

  const hasCustomOffer = computed(() => Boolean(customItem.value));
  const highlightFirstResult = computed(() => toBoolean(props.highlightFirstResult) || hasCustomOffer.value);
  const pickerSelected = computed(() =>
    hasCustomOffer.value ? selected().filter((value) => String(getItemValue(value)) !== search.value) : selected(),
  );
  const offer = computed(() =>
    customItem.value
      ? {
          [pickerItemTitle.value]: customItemTitle(getItemTitle(customItem.value)),
          [pickerItemValue.value]: getItemValue(customItem.value),
          __spSelectCustomOffer: true,
          pinned: true,
        }
      : null,
  );
  // The source copy is excluded from query matching; the result mapper appends the action.
  const pickerItems = computed(() =>
    offer.value ? [...projectedItems.value, { ...offer.value, visibility: "browse" }] : projectedItems.value,
  );

  function syncSearch(value) {
    search.value = String(value ?? "");
  }

  watch(() => props.search, syncSearch, { immediate: true });

  function updateMatches(hasMatch) {
    hasMatches.value = hasMatch;
  }

  function mapResultRows(rows) {
    if (searchTooShort.value) {
      return [];
    }

    return allowCustom.value ? mapMatchedItems(rows) : rows;
  }

  function mapMatchedItems(rows) {
    const matched = rows.filter((item) => !item?.__spSelectCustomOffer);
    if (!offer.value) {
      return matched;
    }

    const existingItems = matched.map((item) =>
      item && typeof item === "object"
        ? { ...item, pendingChoice: false, props: { ...item.props, highlighted: false } }
        : item,
    );
    return [...existingItems, { ...offer.value, props: { highlighted: true } }];
  }

  function withPickerFields(item) {
    if (!item || typeof item !== "object") {
      return item;
    }

    const title = getItemTitle(item);
    const value = getItemValue(item);
    const row = { ...item };
    delete row[props.itemTitle];
    delete row[props.itemValue];

    if (Array.isArray(row.children)) {
      row.children = row.children.map(withPickerFields);
    }

    return { ...row, [pickerItemTitle.value]: title, [pickerItemValue.value]: value };
  }

  function getPickerItemTitle(item) {
    return item && typeof item === "object" ? item[pickerItemTitle.value] : getItemTitle(item);
  }

  function getPickerItemValue(item) {
    return allowCustom.value && item && typeof item === "object" ? item[pickerItemValue.value] : getItemValue(item);
  }

  function updateSearch(text) {
    search.value = typeof text === "string" ? text : "";
  }

  function updateActivatorSearch(text) {
    if (typedSearch.value) {
      updateSearch(text);
    }
  }

  function clearSearch() {
    search.value = "";
  }

  function commitCustomItem(values, previousValues = []) {
    const item = customItem.value;
    if (!item) {
      return values;
    }

    const isCustomValue = (value) => String(value) === String(getItemValue(item));
    if (values.some(isCustomValue)) {
      addCustomItem(item);
      return values;
    }

    if (previousValues.some(isCustomValue)) {
      addCustomItem(item);
      return [...values, getItemValue(item)];
    }

    return values;
  }

  return {
    pickerItemTitle,
    pickerItemValue,
    getPickerItemValue,
    searchable,
    hideSearchBarAttr,
    highlightIdleFirstResult,
    pickerSearch,
    canOpenList,
    highlightFirstResult,
    pickerSelected,
    mapResults,
    noDataMessage,
    updateMatches,
    updateActivatorSearch,
    pickerItems,
    hasCustomOffer,
    updateSearch,
    clearSearch,
    commitCustomItem,
  };
}
